import { Fragment, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { Card } from '@/components/ui/card'
import { formatDateTime } from '@/lib/dates'

/**
 * User activity table — read-only flat list of state-changing user
 * actions from `user_action_log`. Rendered in three places:
 *
 *   1. /admin/activity                  — cross-tenant feed
 *   2. /admin/tenants/:id → User activity tab — single-tenant feed
 *   3. /admin/tenants/:id → Combined tab     — single-tenant feed
 *      interleaved with `assistant_requests` (Eric's bot-side
 *      activity), source-chipped + linked
 *
 * Filter set, expand UX, and chip styling mirror EricActivityTable so
 * platform admins moving between tabs see the same shape.
 *
 * Polls every 10 seconds — same cadence as the Eric table — so Mira
 * watching a live testing session sees rows land without refreshing.
 */

export type UserActionEntry = {
  kind: 'user_action'
  id: string
  shop_id: string
  shop_name: string | null
  actor_user_id: string | null
  actor_clerk_user_id: string | null
  actor_email: string | null
  actor_role: string
  actor_name: string | null
  action: string
  entity_type: string | null
  entity_id: string | null
  payload_diff_json: Record<string, unknown> | null
  is_full_payload: boolean
  source: 'web' | 'api' | 'eric_tool'
  assistant_request_id: string | null
  ip_address: string | null
  user_agent: string | null
  request_id: string | null
  created_at: string
}

export type EricRequestEntry = {
  kind: 'eric_request'
  id: string
  shop_id: string
  shop_name: string | null
  request_text: string
  guardrail_decision: string
  final_reply: string | null
  ms_total: number | null
  created_at: string
  // Actor attribution. clerk_user_id is the firing user when the
  // request came from a signed-in channel; null for anon SMS senders.
  // actor_name + actor_email come from the users join when populated.
  clerk_user_id: string | null
  channel: string
  channel_user_id: string
  actor_name: string | null
  actor_email: string | null
}

type Entry = UserActionEntry | EricRequestEntry

type Resp = {
  entries: UserActionEntry[]
  eric_entries: EricRequestEntry[]
  next_cursor: string | null
}

export type Range = '24h' | '7d' | '30d' | 'all'
export type SourceFilter = 'all' | 'web' | 'api' | 'eric_tool'

export type UserActivityTableProps = {
  shopId?: string
  combined?: boolean
  hideShopColumn?: boolean
  defaultRange?: Range
  refetchIntervalMs?: number
  queryKeyPrefix?: string
}

export function UserActivityTable({
  shopId,
  combined = false,
  hideShopColumn = false,
  defaultRange = '7d',
  refetchIntervalMs = 10_000,
  queryKeyPrefix = 'admin.user-activity',
}: UserActivityTableProps) {
  const { t } = useTranslation('admin')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const [expanded, setExpanded] = useState<string | null>(null)
  const [range, setRange] = useState<Range>(defaultRange)
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all')
  const [actionPrefix, setActionPrefix] = useState<string>('all')
  const [search, setSearch] = useState<string>('')

  const query = useQuery<Resp>({
    queryKey: [
      queryKeyPrefix,
      {
        shopId: shopId ?? null,
        combined,
        range,
        sourceFilter,
        actionPrefix,
        search,
      },
    ],
    enabled: isLoaded,
    refetchInterval: refetchIntervalMs > 0 ? refetchIntervalMs : false,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const params = new URLSearchParams({ limit: '100' })
      if (shopId) params.set('shop_id', shopId)
      if (range !== 'all') params.set('range', range)
      if (sourceFilter !== 'all') params.set('source', sourceFilter)
      if (actionPrefix !== 'all') params.set('action_prefix', actionPrefix)
      if (search.trim()) params.set('search', search.trim())
      if (combined && shopId) params.set('include_eric', '1')
      const res = await fetch(
        `/api/admin/user-activity?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!res.ok) {
        throw new Error(
          tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as Resp
    },
  })

  const entries: Entry[] = [
    ...(query.data?.entries ?? []),
    ...(query.data?.eric_entries ?? []),
  ].sort((a, b) =>
    a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0,
  )

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <label className="flex items-center gap-2">
          <span className="font-semibold uppercase tracking-wide text-muted-foreground">
            {t('user_activity.filter.range')}
          </span>
          <select
            value={range}
            onChange={(e) => setRange(e.target.value as Range)}
            className="rounded-md border border-border bg-background px-2 py-1"
          >
            <option value="24h">{t('user_activity.range.h24')}</option>
            <option value="7d">{t('user_activity.range.d7')}</option>
            <option value="30d">{t('user_activity.range.d30')}</option>
            <option value="all">{t('user_activity.range.all')}</option>
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="font-semibold uppercase tracking-wide text-muted-foreground">
            {t('user_activity.filter.action')}
          </span>
          <select
            value={actionPrefix}
            onChange={(e) => setActionPrefix(e.target.value)}
            className="rounded-md border border-border bg-background px-2 py-1"
          >
            <option value="all">{t('user_activity.action_filter.all')}</option>
            <option value="customer.">
              {t('user_activity.action_filter.customer')}
            </option>
            <option value="vehicle.">
              {t('user_activity.action_filter.vehicle')}
            </option>
            <option value="appointment.">
              {t('user_activity.action_filter.appointment')}
            </option>
            <option value="carryover.">
              {t('user_activity.action_filter.carryover')}
            </option>
            <option value="estimate.">
              {t('user_activity.action_filter.estimate')}
            </option>
            <option value="tag.">
              {t('user_activity.action_filter.tag')}
            </option>
            <option value="settings.">
              {t('user_activity.action_filter.settings')}
            </option>
            <option value="auth.">
              {t('user_activity.action_filter.auth')}
            </option>
            <option value="call.">
              {t('user_activity.action_filter.call')}
            </option>
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="font-semibold uppercase tracking-wide text-muted-foreground">
            {t('user_activity.filter.source')}
          </span>
          <select
            value={sourceFilter}
            onChange={(e) =>
              setSourceFilter(e.target.value as SourceFilter)
            }
            className="rounded-md border border-border bg-background px-2 py-1"
          >
            <option value="all">{t('user_activity.source.all')}</option>
            <option value="web">{t('user_activity.source.web')}</option>
            <option value="eric_tool">
              {t('user_activity.source.eric_tool')}
            </option>
            <option value="api">{t('user_activity.source.api')}</option>
          </select>
        </label>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('user_activity.filter.search_placeholder')}
          className="rounded-md border border-border bg-background px-2 py-1 sm:w-56"
        />
        {query.data ? (
          <span className="text-muted-foreground">
            {t('user_activity.filter.summary', { count: entries.length })}
          </span>
        ) : null}
      </div>

      {query.isError ? (
        <Card className="border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {query.error instanceof Error
            ? query.error.message
            : t('user_activity.error')}
        </Card>
      ) : null}
      {query.isLoading ? (
        <Card className="p-4 text-sm text-muted-foreground">
          {tCommon('loading')}
        </Card>
      ) : null}

      {query.data && entries.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          {t('user_activity.empty')}
        </Card>
      ) : null}

      {query.data && entries.length > 0 ? (
        <>
          {/* Desktop / tablet: table */}
          <Card className="hidden overflow-hidden p-0 md:block">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-3 font-semibold">
                      {t('user_activity.table.when')}
                    </th>
                    <th className="px-4 py-3 font-semibold">
                      {t('user_activity.table.actor')}
                    </th>
                    <th className="px-4 py-3 font-semibold">
                      {t('user_activity.table.action')}
                    </th>
                    <th className="px-4 py-3 font-semibold">
                      {t('user_activity.table.target')}
                    </th>
                    {hideShopColumn ? null : (
                      <th className="px-4 py-3 font-semibold">
                        {t('user_activity.table.tenant')}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => {
                    const isOpen = expanded === e.id
                    return (
                      <Fragment key={`${e.kind}:${e.id}`}>
                        <tr
                          className="cursor-pointer border-b border-border/60 hover:bg-muted/30 last:border-0"
                          onClick={() => setExpanded(isOpen ? null : e.id)}
                        >
                          <td className="whitespace-nowrap px-4 py-3 text-xs text-muted-foreground">
                            {formatDateTime(e.created_at)}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-foreground">
                            {e.kind === 'user_action' ? (
                              <UserActorCell entry={e} />
                            ) : (
                              <EricRequestActorCell entry={e} />
                            )}
                          </td>
                          <td className="px-4 py-3 text-foreground">
                            {e.kind === 'user_action' ? (
                              <ActionChip
                                action={e.action}
                                source={e.source}
                              />
                            ) : (
                              <EricRequestChip entry={e} />
                            )}
                          </td>
                          <td className="px-4 py-3 text-foreground">
                            {e.kind === 'user_action'
                              ? formatTarget(e)
                              : truncate(e.request_text, 60)}
                          </td>
                          {hideShopColumn ? null : (
                            <td className="px-4 py-3 text-xs text-muted-foreground">
                              {e.shop_name ?? e.shop_id}
                            </td>
                          )}
                        </tr>
                        {isOpen ? (
                          <tr className="border-b border-border/60 bg-muted/20">
                            <td
                              colSpan={hideShopColumn ? 4 : 5}
                              className="px-4 py-4"
                            >
                              {e.kind === 'user_action' ? (
                                <UserActionDetail entry={e} />
                              ) : (
                                <EricRequestDetail entry={e} />
                              )}
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Mobile: stacked card list. The horizontal-scroll table
              wrapper above is fine for >=md but cramps a 375px screen,
              so phone gets one card per row. */}
          <div className="space-y-2 md:hidden">
            {entries.map((e) => {
              const isOpen = expanded === e.id
              return (
                <Card
                  key={`${e.kind}:${e.id}`}
                  className="cursor-pointer p-3"
                  onClick={() => setExpanded(isOpen ? null : e.id)}
                >
                  <div className="flex items-start justify-between gap-2 text-xs text-muted-foreground">
                    <span>{formatDateTime(e.created_at)}</span>
                    {hideShopColumn ? null : (
                      <span className="truncate">
                        {e.shop_name ?? e.shop_id}
                      </span>
                    )}
                  </div>
                  <div className="mt-1.5">
                    {e.kind === 'user_action' ? (
                      <ActionChip action={e.action} source={e.source} />
                    ) : (
                      <EricRequestChip entry={e} />
                    )}
                  </div>
                  <div className="mt-1.5 text-sm text-foreground">
                    {e.kind === 'user_action' ? (
                      <>
                        <UserActorCell entry={e} compact />
                        <div className="mt-1 text-xs text-muted-foreground">
                          {formatTarget(e)}
                        </div>
                      </>
                    ) : (
                      <>
                        <EricRequestActorCell entry={e} compact />
                        <div className="mt-1 text-xs text-muted-foreground">
                          {truncate(e.request_text, 80)}
                        </div>
                      </>
                    )}
                  </div>
                  {isOpen ? (
                    <div className="mt-3 border-t border-border/60 pt-3">
                      {e.kind === 'user_action' ? (
                        <UserActionDetail entry={e} />
                      ) : (
                        <EricRequestDetail entry={e} />
                      )}
                    </div>
                  ) : null}
                </Card>
              )
            })}
          </div>
        </>
      ) : null}
    </div>
  )
}

function UserActorCell({
  entry,
  compact = false,
}: {
  entry: UserActionEntry
  compact?: boolean
}) {
  const label =
    entry.actor_name ?? entry.actor_email ?? entry.actor_clerk_user_id ?? '—'
  return (
    <div className="flex items-center gap-1.5">
      <span aria-hidden>👤</span>
      <div>
        <div className={compact ? 'text-sm font-medium' : 'font-medium'}>
          {label}
        </div>
        {entry.actor_role && entry.actor_role !== 'unknown' ? (
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
            {entry.actor_role.replace(/^org:/, '')}
          </div>
        ) : null}
      </div>
    </div>
  )
}

// Eric-request actor: when the row carries a clerk_user_id (signed-in
// channel — dashboard cascade, in-app chat), attribute to that user
// with a "via Eric" subtitle so the audit trail shows who actually
// fired the action. Falls back to "Eric" for genuinely anon-channel
// rows (unrecognized SMS / phone caller), with the channel_user_id
// (phone number) as the subtitle when available.
function EricRequestActorCell({
  entry,
  compact = false,
}: {
  entry: EricRequestEntry
  compact?: boolean
}) {
  const { t } = useTranslation('admin')
  if (entry.clerk_user_id) {
    const label =
      entry.actor_name ??
      entry.actor_email ??
      entry.clerk_user_id ??
      '—'
    return (
      <div className="flex items-center gap-1.5">
        <span aria-hidden>👤</span>
        <div>
          <div className={compact ? 'text-sm font-medium' : 'font-medium'}>
            {label}
          </div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
            via Eric · {entry.channel}
          </div>
        </div>
      </div>
    )
  }
  // No attached user — anon SMS / phone. Show Eric with the channel
  // user id (phone) as subtitle when present.
  const subtitle =
    entry.channel_user_id && entry.channel_user_id !== 'anon'
      ? entry.channel_user_id
      : entry.channel
  return (
    <div className="flex items-center gap-1.5">
      <span aria-hidden>🤖</span>
      <div>
        <div className={compact ? 'text-sm font-medium' : 'font-medium'}>
          {t('user_activity.actor.eric')}
        </div>
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
          {subtitle}
        </div>
      </div>
    </div>
  )
}

function ActionChip({
  action,
  source,
}: {
  action: string
  source: 'web' | 'api' | 'eric_tool'
}) {
  const { t } = useTranslation('admin')
  // Action color groups by prefix.
  const prefix = action.split('.')[0]
  const cls =
    {
      customer: 'bg-blue-100 text-blue-800',
      vehicle: 'bg-cyan-100 text-cyan-800',
      appointment: 'bg-violet-100 text-violet-800',
      carryover: 'bg-amber-100 text-amber-800',
      estimate: 'bg-emerald-100 text-emerald-800',
      tag: 'bg-pink-100 text-pink-800',
      settings: 'bg-slate-100 text-slate-800',
      auth: 'bg-zinc-200 text-zinc-800',
      call: 'bg-orange-100 text-orange-800',
    }[prefix] ?? 'bg-muted text-muted-foreground'
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span
        className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}
      >
        {t(`user_activity.action.${action}`, action)}
      </span>
      {source === 'eric_tool' ? (
        <span
          className="inline-block whitespace-nowrap rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-800"
          title={t('user_activity.via_eric_hint')}
        >
          {t('user_activity.via_eric')}
        </span>
      ) : null}
    </div>
  )
}

function EricRequestChip({ entry }: { entry: EricRequestEntry }) {
  const { t } = useTranslation('admin')
  const decision = entry.guardrail_decision
  const tone: 'ok' | 'warn' | 'bad' =
    decision === 'allow'
      ? 'ok'
      : decision.startsWith('refuse_')
        ? 'warn'
        : 'bad'
  const cls = {
    ok: 'bg-emerald-100 text-emerald-800',
    warn: 'bg-amber-100 text-amber-800',
    bad: 'bg-rose-100 text-rose-800',
  }[tone]
  return (
    <span
      className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}
    >
      {t('user_activity.eric.assistant_request')} · {decision}
    </span>
  )
}

function formatTarget(e: UserActionEntry): string {
  if (!e.entity_type && !e.entity_id) return '—'
  if (e.entity_type && e.entity_id)
    return `${e.entity_type} · ${e.entity_id}`
  return e.entity_type ?? e.entity_id ?? '—'
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s
}

function UserActionDetail({ entry }: { entry: UserActionEntry }) {
  const { t } = useTranslation('admin')
  return (
    <div className="space-y-3 text-xs">
      <div className="grid gap-3 sm:grid-cols-2">
        <DetailRow
          label={t('user_activity.detail.actor')}
          value={
            entry.actor_email ?? entry.actor_clerk_user_id ?? '(unknown)'
          }
        />
        <DetailRow
          label={t('user_activity.detail.role')}
          value={entry.actor_role}
        />
        <DetailRow
          label={t('user_activity.detail.entity')}
          value={
            entry.entity_type && entry.entity_id
              ? `${entry.entity_type} · ${entry.entity_id}`
              : '—'
          }
        />
        <DetailRow
          label={t('user_activity.detail.source')}
          value={entry.source}
        />
        {entry.assistant_request_id ? (
          <DetailRow
            label={t('user_activity.detail.assistant_request')}
            value={entry.assistant_request_id}
          />
        ) : null}
        {entry.ip_address ? (
          <DetailRow
            label={t('user_activity.detail.ip')}
            value={entry.ip_address}
          />
        ) : null}
      </div>
      {entry.payload_diff_json ? (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {entry.is_full_payload
              ? t('user_activity.detail.payload')
              : t('user_activity.detail.diff')}
          </div>
          <pre className="mt-1 max-h-96 overflow-auto rounded border border-border bg-background p-3 font-mono text-[11px] text-foreground">
            {JSON.stringify(entry.payload_diff_json, null, 2)}
          </pre>
        </div>
      ) : null}
      {entry.user_agent || entry.request_id ? (
        <div className="font-mono text-[10px] text-muted-foreground">
          {entry.request_id ? `cf-ray: ${entry.request_id}` : null}
          {entry.request_id && entry.user_agent ? ' · ' : null}
          {entry.user_agent ? `ua: ${truncate(entry.user_agent, 80)}` : null}
        </div>
      ) : null}
    </div>
  )
}

function EricRequestDetail({ entry }: { entry: EricRequestEntry }) {
  const { t } = useTranslation('admin')
  return (
    <div className="space-y-2 text-xs">
      <DetailRow
        label={t('user_activity.eric.request')}
        value={entry.request_text}
      />
      <DetailRow
        label={t('user_activity.eric.reply')}
        value={entry.final_reply ?? '—'}
      />
      <div className="font-mono text-[10px] text-muted-foreground">
        {t('user_activity.detail.assistant_request')}: {entry.id}
        {entry.ms_total != null ? ` · ${entry.ms_total} ms` : ''}
      </div>
    </div>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 whitespace-pre-wrap font-mono text-foreground">
        {value}
      </div>
    </div>
  )
}
